import { Icon } from '../../Icon'
import { formatPhone } from '../resourcesData'
import type { QuickRefContact } from '../../../types'

// Key Contacts tab — prototype L3746-3761. One row per distinct on-duty
// user, keyed to their highest-priority roster role for this event
// (lowest Roles.sort_order wins; ties on alphabetical name). Phone NULL
// → "—" placeholder. Empty state when no roster assignments exist yet.

interface Props {
  contacts: QuickRefContact[]
}

export function KeyContactsTab({ contacts }: Props) {
  return (
    <div>
      <h3 className="font-display text-xl font-semibold mb-3">Key Contacts — On Duty Today</h3>

      {contacts.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left py-2 pr-3 font-semibold text-xs uppercase tracking-wider text-ink-soft">Role</th>
                <th className="text-left py-2 pr-3 font-semibold text-xs uppercase tracking-wider text-ink-soft">Name</th>
                <th className="text-left py-2 font-semibold text-xs uppercase tracking-wider text-ink-soft">Phone</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => <ContactRow key={c.userId} contact={c} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ContactRow({ contact }: { contact: QuickRefContact }) {
  return (
    <tr className="border-b border-line/60 last:border-b-0">
      <td className="py-2 pr-3 align-middle">{contact.rosterRole}</td>
      <td className="py-2 pr-3 align-middle">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-brass-soft text-brass text-[9px] font-bold flex-shrink-0"
          >
            {contact.avatarInitials ?? '?'}
          </span>
          <span className="font-semibold truncate">{contact.name}</span>
        </div>
      </td>
      <td className="py-2 align-middle">
        {contact.phone ? (
          <a
            href={`tel:${contact.phone}`}
            className="font-mono text-sm text-green hover:underline"
          >
            {formatPhone(contact.phone)}
          </a>
        ) : (
          <span className="text-ink-soft">—</span>
        )}
      </td>
    </tr>
  )
}

function EmptyState() {
  return (
    <div className="bg-parchment border border-line rounded-lg p-5 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brass-soft text-brass mb-2">
        <Icon name="users" size={20} />
      </div>
      <div className="font-semibold text-sm mb-1">No roster assignments yet</div>
      <div className="text-xs text-ink-soft max-w-sm mx-auto leading-snug">
        Once the roster is built for this event, on-duty staff and their phone numbers
        appear here. Add via the event’s Roster tab.
      </div>
    </div>
  )
}
